import { createEffect, createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import type { TodoStore } from "../lib/store";
import { TaskItem } from "./TaskItem";

const ROW_HEIGHT = 74;
const OVERSCAN = 8;

export function TaskList(props: { store: TodoStore; onAddTask: () => void }) {
  let scroller: HTMLDivElement | undefined;
  const [scrollTop, setScrollTop] = createSignal(0);
  const [viewportHeight, setViewportHeight] = createSignal(640);

  const windowed = createMemo(() => {
    const tasks = props.store.visibleTasks();
    const start = Math.max(0, Math.floor(scrollTop() / ROW_HEIGHT) - OVERSCAN);
    const visibleCount = Math.ceil(viewportHeight() / ROW_HEIGHT) + OVERSCAN * 2;
    const end = Math.min(tasks.length, start + visibleCount);
    return {
      tasks,
      rows: tasks.slice(start, end),
      start,
      end,
      top: start * ROW_HEIGHT,
      bottom: Math.max(0, (tasks.length - end) * ROW_HEIGHT),
    };
  });

  onMount(() => {
    if (!scroller) return;
    const resize = new ResizeObserver(([entry]) => {
      setViewportHeight(entry?.contentRect.height ?? 640);
    });
    resize.observe(scroller);
    onCleanup(() => resize.disconnect());
  });

  createEffect(() => {
    const id = props.store.selectedId();
    const element = scroller;
    if (!id || !element) return;
    const index = props.store.visibleTasks().findIndex((task) => task.id === id);
    if (index === -1) return;
    const rowTop = index * ROW_HEIGHT;
    const rowBottom = rowTop + ROW_HEIGHT;
    if (rowTop < element.scrollTop) {
      element.scrollTop = rowTop;
    } else if (rowBottom > element.scrollTop + element.clientHeight) {
      element.scrollTop = rowBottom - element.clientHeight;
    }
  });

  return (
    <div
      ref={scroller}
      class="task-list"
      role={windowed().tasks.length > 0 ? "list" : undefined}
      aria-label={windowed().tasks.length > 0 ? "Task list" : undefined}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
    >
      <Show
        when={windowed().tasks.length > 0}
        fallback={
          <section class="empty-state">
            <div class="empty-check" aria-hidden="true">Done</div>
            <h2>All clear!</h2>
            <p>There is nothing to do here right now.</p>
            <button class="primary-button" onClick={props.onAddTask}>Add a task</button>
          </section>
        }
      >
        <div style={{ height: `${windowed().top}px` }} aria-hidden="true" />
        <For each={windowed().rows}>
          {(task, index) => (
            <TaskItem
              task={task}
              store={props.store}
              position={windowed().start + index() + 1}
              setSize={windowed().tasks.length}
            />
          )}
        </For>
        <div style={{ height: `${windowed().bottom}px` }} aria-hidden="true" />
      </Show>
    </div>
  );
}
